import React, { useMemo } from "react"
import { Link } from "react-router-dom"
import { BellRing, BriefcaseBusiness, ChevronRight, MessageSquareText, Store, Vote } from "lucide-react"
import useNotifications from "../../hooks/DataFetching/useNotifications"
import { getNotificationTarget } from "../../lib/notificationTargets"

const formatTime = (value) => {
  if (!value) return "Recent"
  return new Date(value).toLocaleDateString(undefined, { month: "short", day: "numeric" })
}

export default function DashboardActivityPanel({
  inventoryCount = 0,
  isInventoryLoading = false,
  jobsPayrollEnabled = false,
  marketplaceTradesEnabled = false,
  totalVotes = 0,
}) {
  const { notifications, isConnected } = useNotifications()

  const recentNotifications = useMemo(
    () => notifications.slice(0, 4),
    [notifications],
  )

  const activityCards = [
    {
      label: "Votes recorded",
      value: totalVotes,
      icon: Vote,
      to: "/dashboard/votes",
      tone: "cyan",
    },
    {
      label: "Inventory items",
      value: isInventoryLoading ? "..." : inventoryCount,
      icon: Store,
      to: "/marketplace",
      tone: "amber",
      hidden: !marketplaceTradesEnabled,
    },
    {
      label: "Role activity",
      value: jobsPayrollEnabled ? "Open" : "Limited",
      icon: BriefcaseBusiness,
      to: "/jobs",
      tone: "emerald",
    },
  ].filter((card) => !card.hidden)

  return (
    <section className="dashboard-activity-panel" aria-labelledby="dashboard-activity-title">
      <div className="dashboard-activity-header">
        <div>
          <span>Latest activity</span>
          <h2 id="dashboard-activity-title">Stay close to what changed</h2>
        </div>
        <span className={`dashboard-activity-status ${isConnected ? "online" : ""}`}>
          {isConnected ? "Live" : "Refreshing"}
        </span>
      </div>

      <div className="dashboard-activity-metrics">
        {activityCards.map((card) => {
          const Icon = card.icon

          return (
            <Link key={card.label} to={card.to} className={`dashboard-activity-metric ${card.tone}`}>
              <Icon size={18} aria-hidden="true" />
              <span>{card.label}</span>
              <strong>{card.value}</strong>
            </Link>
          )
        })}
      </div>

      <div className="dashboard-activity-list">
        {recentNotifications.length ? (
          recentNotifications.map((notification) => (
            <Link
              key={notification.id}
              to={getNotificationTarget(notification)}
              className={`dashboard-activity-item${notification.isRead ? "" : " unread"}`}
            >
              <BellRing size={17} aria-hidden="true" />
              <span>
                <strong>{notification.title || "Notification"}</strong>
                <small>{notification.content || "Open to view details."}</small>
              </span>
              <em>{formatTime(notification.createdAt)}</em>
            </Link>
          ))
        ) : (
          <div className="dashboard-activity-empty">
            <MessageSquareText size={18} aria-hidden="true" />
            <span>
              <strong>No recent alerts</strong>
              <small>New replies, offers, requests, and campaign updates will appear here.</small>
            </span>
          </div>
        )}
      </div>

      <Link to="/settings" className="dashboard-activity-settings">
        Tune alert preferences
        <ChevronRight size={16} aria-hidden="true" />
      </Link>
    </section>
  )
}
